import * as React from 'react'
import isEmpty from 'lodash/isEmpty'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Popup from '../../../ui/popup'
import Button from '../../../ui/button'
import TextField from '../../../form/TextField'

import { Formik, Form, Field, FieldProps, FormikProps } from 'formik'
import * as Yup from 'yup'

import { ChangeEmail, MemberState, ValidateEmail } from '../../../../store/member/memberTypings'

import { changeEmail, validateEmail } from '../../../../api/member'
import { memberFetch } from '../../../../store/member/memberSlice'
import { RootState } from '../../../../store/rootReducer'
import cls from './changeEmailPopup.module.sass'

interface Props {
    onClose: () => void
}

const ChangeEmailPopup: React.FunctionComponent<Props> = ({ onClose }: Props) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()

    const { user } = useSelector<RootState, MemberState>((state) => state.member)
    const [message, setMessage] = useState('')

    type FormValues = {
        code: string
        email: string
        email_confirm: string
    }

    const initialValues: FormValues = {
        code: '',
        email: '',
        email_confirm: ''
    }

    const validationSchema = Yup.object().shape({
        email: Yup.string().email(t('Invalid email')).required(t('Email is required')),
        email_confirm: Yup.string()
            .oneOf([Yup.ref('email'), null], t('Emails must match'))
            .required(t('Email confirmation is required'))
    })

    return isEmpty(message) ? (
        <Popup title={t('Change email')} onClose={onClose} classname="vertical" profile={true}>
            <div className={cls['currentEmail']}>
                <p>{t('Current email address')}</p>
                <p>{user.email}</p>
            </div>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values: ChangeEmail, formikHelpers) => {
                    changeEmail(values)
                        .then((res: { message: string }) => {
                            setMessage(res.message)
                        })
                        .catch((err) => {
                            formikHelpers.setErrors({ email: err.messages[0] })
                        })
                }}
            >
                {(props: FormikProps<ChangeEmail>) => {
                    const { submitForm } = props
                    return (
                        <Form className="form">
                            <Field name="email">
                                {({ field, meta }: FieldProps) => (
                                    <TextField
                                        type="email"
                                        field={field}
                                        label={t('New email address')}
                                        error={meta.error}
                                    />
                                )}
                            </Field>
                            <Field name="email_confirm">
                                {({ field, meta }: FieldProps) => (
                                    <TextField
                                        type="email"
                                        field={field}
                                        label={t('New email address again')}
                                        error={meta.error}
                                    />
                                )}
                            </Field>
                            <div className="popup__buttons">
                                <Button label={t('Save')} type="button" onClick={submitForm} />
                                <Button label={t('Cancel')} secondary onClick={onClose} />
                            </div>
                        </Form>
                    )
                }}
            </Formik>
        </Popup>
    ) : (
        <Popup
            onClose={onClose}
            classname="vertical"
            title={t('Validate email')}
            buttons={
                <button type="submit" className="button" form="myform">
                    {t('Send')}
                </button>
            }
        >
            <Formik
                validateOnChange={false}
                initialValues={initialValues}
                validationSchema={Yup.object().shape({
                    code: Yup.string().required(t('Validation token is required'))
                })}
                onSubmit={(values: ValidateEmail, formikHelpers): void => {
                    validateEmail(values)
                        .then(() => {
                            dispatch(memberFetch({}))
                            onClose()
                        })
                        .catch((err) => {
                            formikHelpers.setErrors({ code: err.message })
                        })
                }}
            >
                <Form className="form" id="myform">
                    <div className="form__message">{message}</div>
                    <Field name="code">
                        {({ field, meta }: FieldProps) => (
                            <TextField
                                field={field}
                                type="text"
                                label={t('Validation code')}
                                error={meta.touched && meta.error}
                            />
                        )}
                    </Field>
                </Form>
            </Formik>
        </Popup>
    )
}

export default ChangeEmailPopup
